<template>
  <div class="search-wrapper">

    <div class="search-bar">
      <label for="query" class="sr-only">Search</label>
      <input
        ref="searchField"
        id="query"
        class="search-field"
        type="text"
        maxlength="500"
        autocomplete="off"
        title="Suche"
        spellcheck="false"
        v-model="query"
        @keyup.enter="setListStyle(false)"
      />
      <span v-if="hasQuery && !loading" class="search-status-indicator">
        <button @click="clearSearch" aria-controls="query">&times;<span class="sr-only">Suche abbrechen</span></button>
      </span>
      <span v-else-if="loading" class="search-status-indicator">
        <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid"><circle cx="50" cy="50" r="25" stroke-width="10" stroke="#bababa" stroke-dasharray="39.269908169872416 39.269908169872416" fill="none" stroke-linecap="round">
  <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="1.333333333333333s" keyTimes="0;1" values="0 50 50;360 50 50"></animateTransform>
</circle></svg>
      </span>
    </div>

    <div :class="{ 'result-wrapper': true, 'result-wrapper-autocomplete': useAutocompleteStyle, 'result-wrapper-list': !useAutocompleteStyle  }">
        <div v-if="!loading && totalHits > 0">
          <div class="result-list-header" v-if="!useAutocompleteStyle">
            Es
            <template v-if="totalHits > 1">wurden</template><template v-else>wurde</template>
            {{ totalHits }}
            <template v-if="totalHits > 1">Einträge</template><template v-else>Eintrag</template>
            gefunden:
          </div>

          <ol class="results" v-if="hits.length > 0">
            <li v-for="hit in hits" :key="hit.id">
              <router-link :to="{ name: 'entry', params: { id: hit.id }}">
                <h3>{{ hit.data.name }}</h3>
                <p>{{ hit.data.description }}</p>
              </router-link>
            </li>
            <li class="list-footer" v-if="useAutocompleteStyle">
              Es
              <template v-if="totalHits > 1">wurden</template><template v-else>wurde</template>
              {{ totalHits }}
              <template v-if="totalHits > 1">Einträge</template><template v-else>Eintrag</template>
              gefunden.
            </li>
          </ol>

          <div v-if="!useAutocompleteStyle" class="page-navigation">
            <button v-if="hasPrevPage" @click="prevPage()" class="button">Vorherige Seite</button>
            <span class="page-navigation-current-page">Seite {{ currentPage }} von {{ totalPages }}</span>
            <button v-if="hasNextPage" @click="nextPage()" class="button">Nächste Seite</button>
          </div>

          <div v-if="totalHits == 0 && !loading" class="no-results">
            <p>Es wurden keine Einträge für „{{ query }}“ gefunden.</p>
            <p class="suggest-stadtkatalog-entry">
              <a :href="proposeEntryUrl" class="button">
                Neuen Eintrag vorschlagen
              </a>
            </p>
          </div>
        </div>

    </div>

    <div v-if="error !== ''" class="error">
      <h1>Fehler aufgetreten</h1>
      <p>{{ error }}</p>
    </div>

  </div>
</template>

<script>
  import debounce from "lodash.debounce";
  import { mapState, mapGetters } from "vuex";

  const MIN_QUERY_LENGTH = 3;

  export default {
    name: "sk-search",
    data () {
      return {
        useAutocompleteStyle: true,
        error: ""
      }
    },
    computed: {
      query: {
        get () {
          return this.$store.state.query;
        },
        set (value) {
          this.$store.commit("setQuery", value);
        }
      },
      ...mapState([
        "totalHits",
        "hits",
        "querySize",
        "queryFrom",
        "geoFenceName",
        "loading",
      ]),
      ...mapGetters([
        "totalPages",
        "currentPage",
        "hasPrevPage",
        "hasNextPage",
      ]),
      hasQuery: function() {
        return this.query && this.query.length >= MIN_QUERY_LENGTH;
      },
      proposeEntryUrl: function() {
        return `https://app.stadtkatalog.org/stk/proposeEntry${this.geoFenceName !== null ? "?geoFenceName=" + this.geoFenceName : ""}`;
      }
    },
    methods: {
      queryStadtKatalog: function() {
        this.$store.dispatch("updateHits")
          .then(() => {
            // track a site search
            if (window._paq && this.totalHits >= 0) {
              window._paq.push(["trackSiteSearch", this.query, false, this.totalHits]);
            }
          })
          .catch(e => {
            if (e.message === "Failed to fetch") {
              this.error = "Verbindung zum StadtKatalog konnte nicht hergestellt werden.";
            } else {
              this.error = "StadtKatalog konnte nicht abgefragt werden.";
            }
          });
      },
      debouncedQueryStadtKatalog: debounce(function() {
        if (this.query.length >= MIN_QUERY_LENGTH) {
          // eager setLoading to prevent flashes in the autocomplete
          this.$store.commit("setLoading", true);
          this.setListStyle(true);
          this.queryStadtKatalog();
        } else if (this.query === "") {
          this.$store.commit("resetQueryStates");
        }
      }, 450),
      focusSearchField: function() {
        this.$refs.searchField.focus();
      },
      clearSearch: function() {
        this.query = "";
        this.$refs.searchField.focus();
      },
      setListStyle: function(useAutocomplete) {
        this.useAutocompleteStyle = useAutocomplete === true;
        const url = new URL(document.location);
        if (this.useAutocompleteStyle) {
          url.searchParams.delete("q");
          window.history.replaceState( {}, document.title, url);
        } else {
          url.searchParams.set("q", encodeURIComponent(this.query))
          window.history.replaceState( {}, document.title, url);
        }
      },
      nextPage: function() {
        if (this.queryFrom + this.querySize < this.totalHits) {
          this.$store.commit("setQueryFrom", this.queryFrom + this.querySize);
          this.queryStadtKatalog();
        }
      },
      prevPage: function() {
        if (this.queryFrom > 0) {
          this.$store.commit("setQueryFrom", Math.max(this.queryFrom - this.querySize, 0));
          this.queryStadtKatalog();
        }
      }
    },
    created() {
      this.$store.commit("resetQueryStates");
      const params = (new URL(document.location)).searchParams;
      const query = decodeURIComponent(params.get("q") || "");
      if (query.length >= MIN_QUERY_LENGTH) {
        this.query = decodeURIComponent(query);
        this.setListStyle(false);
        this.queryStadtKatalog();
      }
      this.$watch("query", function() {
        this.debouncedQueryStadtKatalog()
      });
    },
    mounted() {
      this.focusSearchField();
    }
  }
</script>

<style lang="scss" scoped>
  @import '~SiteVariables';

  $text-inset: 0.9375rem;
  $hit-whitespace: 1.375rem;

  .search-bar {
    margin-top: 2.5rem;
    position: relative;

    &::before {
      z-index: 1000;
      font-family: "Ionicons";
      font-weight: normal;
      font-style: normal;
      display: inline-block;
      speak: none;
      font-variant: normal;
      text-transform: none;
      text-rendering: auto;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;

      line-height: 2.25rem;
      font-size: 2.25rem;

      content: "\f4a4";
      position: absolute;
      left: $text-inset;
      top: calc(50% - 1.125rem);
      bottom: 0;
    }
  }

  $search-field-padding-right: 4.75rem;
  .search-field {
    -webkit-appearance: textfield;
    width: 100%;
    margin: 0;
    padding: 1.125rem $search-field-padding-right 1.125rem $text-inset+2.5rem;
    border: 1px solid #ddd;
    font-size: 1.375rem;
    border-radius: 0.175rem;
    box-shadow: 0 0 0.5rem #eee;

    outline: none;

    &::-ms-clear {
      display: none;
    }

    &:focus {
      box-shadow: 0 0 0.5rem #ccc;
    }
  }

  .search-status-indicator {
    display: flex;
    position: absolute;
    top: 4px;
    bottom: 4px;
    right: 1px;
    width: $search-field-padding-right;
    justify-content: center;

    > button {
      display: block;
      font-size: 2rem;
      background: transparent;
      color: #bababa;
      margin: 0;
      border: 0;
      width: 100%;

      &:focus:hover {
        outline: none;
      }
    }

    > svg {
      display: block;
      max-width: 2rem;
    }
  }

  .result-wrapper {
    position: relative;
    width: calc(100% - 2px - 1rem);
    margin: 0 0.5rem;
    padding: 0;

    .result-list-header {
      font-size: 1.1875rem;
      text-align: left;
      padding: 26px 0.9375rem 0 0.9375rem;
    }
  }

  .no-results {
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    padding: $text-inset+1rem $text-inset;
    list-style: none;
    background: $search-no-result-background;
    margin: 0 0 10rem;
    box-shadow: 0 0 0.5rem #ccc;
    text-align: center;

    .suggest-stadtkatalog-entry {
      padding-top: 1.25rem;
    }
  }

  .results {
    .result-wrapper-autocomplete & {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      padding: 0;
      background: $search-result-background;
      box-shadow: $search-result-box-shadow;
      margin: 0 0 10rem;
    }

    .result-wrapper-list & {
      position: static;
      margin: 10px 0 0 0;
      padding: 0;
    }

    list-style: none;

    li {
      &:first-child {
        padding: 4px 0 0 0;
      }

      padding: 0;
      margin: 0;

      &:not(.list-footer):hover {
        background: #fff;
      }

      &.list-footer {
        color: #888;
        text-align: center;
        font-size: 1rem;
        padding: $text-inset+0.0625rem $text-inset;
      }
    }

    a {
      display: block;
      color: inherit;
      text-decoration: none;
      padding: $text-inset+0.0625rem $text-inset;
    }

    h3 {
      display: inline;
      margin: 0;
      font-size: 1.1875rem;
      color: $link-search-result-color;
      font-weight: 700;
      text-decoration: underline;
    }
    p {
      margin: 0.1875rem 0 0 0;
      font-size: 1rem;
      line-height: 1.375rem;
    }
  }

  .error {
    text-align: center;
    padding: 1.75rem;

    background: #ff4e3f;
    color: #fff;

    h1 {
      font-size: 1.5rem;
      margin: 0.5rem 0;
    }

    p {
      font-size: 1rem;
      margin: 0;
    }
  }

  .page-navigation {
    padding: 25px 0;

    .page-navigation-current-page {
      padding: 0 10px;
    }
  }

  @media (max-width: 50rem) {
    .result-wrapper {
      width: calc(100% - 2px);
      margin: 0 0 0 1px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/StadtKatalogSearch.vue